import ArrowCollapseIcon from '@persona-id/design-tokens/icons/ArrowCollapseIcon';
import ArrowExpandIcon from '@persona-id/design-tokens/icons/ArrowExpandIcon';
import { FC, PropsWithChildren, RefObject, useContext, useEffect, useRef } from 'react';

import {
  CollapseButton,
  HorizontalDragHandler,
  LeftPanel,
  LeftPanelSharedProps,
  PanelContent,
  PanelTitle,
  PanelTitleActions,
} from 'dashboard/components-lib/NavAndPanelsOverlay/LeftPanelShared';
import { NavAndPanelsOverlayContext } from 'dashboard/components-lib/NavAndPanelsOverlay/NavAndPanelsOverlay';
import ToolbarControlButton from 'dashboard/components-lib/ToolbarControlButton';
import useToggle from 'lib/hooks/useToggle';

const DEFAULT_CONTENT_WIDTH_IN_PERCENT = 50;
const MIN_PANEL_WIDTH_IN_PX = 400;
const MAX_PANEL_WIDTH_IN_PX = 1200;
const MIN_DRAGGABLE_WIDTH_IN_PERCENT = 30;
const MAX_DRAGGABLE_WIDTH_IN_PERCENT = 70;

const boundedWidthStyle = (
  desiredContentWidthInPercent: number,
  rightColumnWidthInPx: number,
): string => {
  // Bind the relative width (compared to the content underneath) of the panel
  const boundedWidthInPercent = `min(max(calc(${desiredContentWidthInPercent}% + ${rightColumnWidthInPx}px), calc(${MIN_DRAGGABLE_WIDTH_IN_PERCENT}% + ${rightColumnWidthInPx}px)), ${MAX_DRAGGABLE_WIDTH_IN_PERCENT}%)`;

  // Hard bind the width of the panel to pixel values (mostly relevant for window resizing)
  return `min(max(${boundedWidthInPercent}, ${MIN_PANEL_WIDTH_IN_PX + rightColumnWidthInPx}px), ${MAX_PANEL_WIDTH_IN_PX}px)`;
};

interface LargeLeftPanelProps extends LeftPanelSharedProps {
  rightColumnRef?: RefObject<HTMLDivElement>;
}

const LargeLeftPanel: FC<PropsWithChildren<LargeLeftPanelProps>> = ({
  title,
  basePath,
  rightColumnRef,
  children,
}) => {
  const { panelsOverlayRef } = useContext(NavAndPanelsOverlayContext);
  // Ref for the whole panel (includes content + right column)
  const panelRef = useRef<HTMLDivElement>(null);

  const [isExpanded, { enable: enableExpanded, disable: disableExpanded }] = useToggle(false);
  // Keeps track of the width of the panel content (NOT including the right column) before it is expanded.
  // The panel gets reset to this width when it is un-expanded.
  const storedContentWidthInPercent = useRef<number | null>(null);

  // Initialize panel width
  useEffect(() => {
    if (panelRef.current) {
      const rightColumnWidthInPx = rightColumnRef?.current?.getBoundingClientRect().width ?? 0;
      panelRef.current.style.width = boundedWidthStyle(
        DEFAULT_CONTENT_WIDTH_IN_PERCENT,
        rightColumnWidthInPx,
      );
    }
  }, [panelRef, rightColumnRef]);

  // Adjust the size of the panel when the right column is opened/closed
  useEffect(() => {
    const rightColumnRefCurrent = rightColumnRef?.current;
    const panelRefCurrent = panelRef.current;
    if (rightColumnRefCurrent == null || panelRefCurrent == null) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      // When the panel is fully expanded, don't change anything and keep the width at 100%
      if (isExpanded) return;

      const rightColumnWidthInPx = rightColumnRefCurrent.getBoundingClientRect().width;
      const contentWidthInPercent =
        storedContentWidthInPercent.current ?? DEFAULT_CONTENT_WIDTH_IN_PERCENT;
      panelRefCurrent.style.width = boundedWidthStyle(contentWidthInPercent, rightColumnWidthInPx);
    });

    resizeObserver.observe(rightColumnRefCurrent);
    return () => {
      resizeObserver.unobserve(rightColumnRefCurrent);
    };
  }, [rightColumnRef, isExpanded]);

  return (
    <LeftPanel ref={panelRef}>
      {!isExpanded && (
        <HorizontalDragHandler
          panelRef={panelRef}
          onMoveTo={(cursorX) => {
            // The panel width is dynamically calculated to scale with the overlay and ranges between
            // ~50% and 70% of the overlay's width. This allows the panel to scale relatively with the
            // window when the window is resized. We additionally bound the panel width by pixel values
            // to enforce an absolute minimum and maximum width.
            if (panelRef.current) {
              const overlayWidthInPx = panelsOverlayRef?.current?.clientWidth ?? 0;
              const rightColumnWidthInPx =
                rightColumnRef?.current?.getBoundingClientRect().width ?? 0;
              const panelX = panelRef.current.getBoundingClientRect().x;

              // boundedWidthStyle caps the max width of the panel via styling as a safeguard,
              // but calculate the actual capped width here for storage in storedContentWidthInPercent
              const maxRelativePanelWidthInPx =
                overlayWidthInPx * (MAX_DRAGGABLE_WIDTH_IN_PERCENT / 100);
              const newContentWidthInPx =
                Math.min(cursorX - panelX, maxRelativePanelWidthInPx) - rightColumnWidthInPx;
              const newContentWidthInPercent = (newContentWidthInPx / overlayWidthInPx) * 100;
              storedContentWidthInPercent.current = newContentWidthInPercent;
              panelRef.current.style.width = boundedWidthStyle(
                newContentWidthInPercent,
                rightColumnWidthInPx,
              );
            }
          }}
        />
      )}

      <PanelTitle>
        {title}
        <PanelTitleActions>
          <ToolbarControlButton
            id='panel__fill-canvas-button'
            icon={isExpanded ? <ArrowCollapseIcon /> : <ArrowExpandIcon />}
            tooltipText={isExpanded ? 'Minimize' : 'Maximize'}
            onClick={() => {
              const rightColumnWidthInPx =
                rightColumnRef?.current?.getBoundingClientRect().width ?? 0;
              if (panelRef.current) {
                if (isExpanded) {
                  disableExpanded();
                  const contentWidthInPercent =
                    storedContentWidthInPercent.current ?? DEFAULT_CONTENT_WIDTH_IN_PERCENT;
                  panelRef.current.style.width = boundedWidthStyle(
                    contentWidthInPercent,
                    rightColumnWidthInPx,
                  );
                } else {
                  enableExpanded();
                  panelRef.current.style.width = '100%';
                }
              }
            }}
          />
          <CollapseButton basePath={basePath} />
        </PanelTitleActions>
      </PanelTitle>
      <PanelContent>{children}</PanelContent>
    </LeftPanel>
  );
};

export default LargeLeftPanel;
